import { useState, useCallback, useMemo } from "react";
import { useLingui } from "@lingui/react/macro";
import { Trans } from "@lingui/macro";
import { TariffVersionsTable } from "@/components/billing/tariffs/TariffVersionsTable";
import { TariffUnifiedGrid } from "@/components/billing/tariffs/TariffUnifiedGrid";
import { TariffVersionFormWithEnums } from "@/components/billing/tariffs/TariffVersionFormWithEnums";
import { TariffBulkActions } from "@/components/billing/tariffs/TariffBulkActions";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { SegmentedToggle } from "@/components/ui/segmented-toggle";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import {
  useTariffVersionsPaginated,
  useTariffWithRates,
  useCreateTariff,
  useUpdateTariff,
  useUpdateTariffRates,
  toTariffRateRequests,
  useActivateTariff,
  useArchiveTariff,
  type TariffVersionsPaginatedOptions,
} from "@/hooks/useTariffs";
import type {
  TariffVersion,
  TariffRate,
  CreateTariffVersionRequest,
} from "@/types/billing";
import type { billing_UpdateTariffVersionRequest } from "@/api";
import { GroupType } from "@/types/billing";
import type {
  TariffVersionFormData,
  TariffVersionFormInput,
} from "@/schemas/tariffValidationSchemas";
import type { SortingState, ColumnFiltersState } from "@tanstack/react-table";

const DEFAULT_PAGE_SIZE = 10;

// Map frontend column IDs to backend sort_by values
const SORT_COLUMN_MAP: Record<string, string> = {
  name: 'name',
  description: 'description',
  groupType: 'group_type',
  status: 'status',
  createdAt: 'created_at',
};

export default function TariffsPage() {
  const { i18n } = useLingui();
  const [selectedVersion, setSelectedVersion] = useState<TariffVersion | null>(
    null
  );
  const [viewMode, setViewMode] = useState<"list" | "edit">("list");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingVersion, setEditingVersion] = useState<TariffVersion | null>(null);

  // Ref to pass adjustment to TariffUnifiedGrid
  const [pendingAdjustment, setPendingAdjustment] = useState<{ type: 'percentage' | 'fixed'; operation: 'increase' | 'decrease'; value: number; services?: string[]; days?: number[] } | null>(null);

  // Ref to pass copy operation to TariffUnifiedGrid
  const [pendingCopy, setPendingCopy] = useState<{ sourceService: string; targetService: string } | null>(null);

  // Toggle for current (draft+active) vs all tariffs
  const [showCurrent, setShowCurrent] = useState(true);

  // Server-side filter state - driven by showCurrent toggle
  const [serverFilters, setServerFilters] = useState<{ status?: string }>({});

  // Server-side sorting state - default to created_at desc
  const [serverSorting, setServerSorting] = useState<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>({
    sortBy: 'created_at',
    sortOrder: 'desc',
  });

  // Server-side search state
  const [serverSearch, setServerSearch] = useState<string>('');

  // Build options for the query
  const queryOptions: TariffVersionsPaginatedOptions = useMemo(() => ({
    status: showCurrent ? 'DRAFT,ACTIVE' : serverFilters.status,
    search: serverSearch || undefined,
    sortBy: serverSorting.sortBy,
    sortOrder: serverSorting.sortOrder,
  }), [showCurrent, serverFilters.status, serverSearch, serverSorting.sortBy, serverSorting.sortOrder]);

  // Data fetching hooks - use paginated hook for server-side pagination
  const {
    data: tariffs,
    total,
    isLoading,
    isFetching,
    pagination,
    onPaginationChange,
    error,
    refetch,
  } = useTariffVersionsPaginated(DEFAULT_PAGE_SIZE, queryOptions);
  
  // Always call the hook, but pass the version ID only when available
  const {
    tariff: _selectedTariff,
    rates: selectedRates,
    isLoading: ratesLoading,
  } = useTariffWithRates(selectedVersion?.id || "");
  

  // Mutation hooks with progress tracking options
  const createTariff = useCreateTariff();
  const updateTariff = useUpdateTariff();
  // "Save & activate" saves silently: the activation toast reports the action.
  const saveBeforeActivate = useUpdateTariff({ silent: true });
  const updateRates = useUpdateTariffRates({
    enableChunking: true, // Enable chunking by default
    maxRetries: 3, // Retry failed requests up to 3 times
  });
  const activateTariff = useActivateTariff();
  const archiveTariff = useArchiveTariff();

  // Handle viewing a tariff version (navigate to unified grid)
  const handleView = useCallback((version: TariffVersion) => {
    setSelectedVersion(version);
    setViewMode("edit");
  }, []);

  // Handle editing a tariff version (open edit dialog)
  const handleEdit = useCallback((version: TariffVersion) => {
    setEditingVersion(version);
    setShowEditDialog(true);
  }, []);

  // Handle copying a tariff version
  const handleCopy = useCallback((version: TariffVersion) => {
    setSelectedVersion(version);
    setShowCreateDialog(true);
  }, []);

  // Handle archiving a tariff version
  const handleArchive = useCallback(
    async (version: TariffVersion) => {
      if (version.id) {
        await archiveTariff.mutateAsync(version.id);
        setSelectedVersion(null);
        setViewMode("list");
      }
    },
    [archiveTariff]
  );

  // Handle creating new tariff
  const handleCreateNew = useCallback(() => {
    setSelectedVersion(null);
    setShowCreateDialog(true);
  }, []);

  // Handle saving edited tariff version
  const handleSaveEditedTariff = useCallback(
    async (data: TariffVersionFormData) => {
      if (!editingVersion?.id) return;
      
      // Transform the form data to match the API update request structure
      const updateData: billing_UpdateTariffVersionRequest = {
        name: data.name,
        description: data.description,
        status: data.status as billing_UpdateTariffVersionRequest['status'],
        age_transition_months: data.age_transition_months,
        age_transition_months_subsidised: data.age_transition_months_subsidised,
        age_transition_timing:
          data.age_transition_timing as billing_UpdateTariffVersionRequest['age_transition_timing'],
        rounding_parents_enabled: data.rounding_parents_enabled,
        rounding_parents_amount: data.rounding_parents_amount,
        rounding_subsidy_enabled: data.rounding_subsidy_enabled,
        rounding_subsidy_amount: data.rounding_subsidy_amount,
      };

      await updateTariff.mutateAsync({ id: editingVersion.id, data: updateData });
      
      setShowEditDialog(false);
      setEditingVersion(null);
      
      // Refresh the list to show updated data
      refetch();
    },
    [editingVersion, updateTariff, refetch]
  );

  // Handle saving new tariff version
  const handleSaveNewTariff = useCallback(
    async (data: TariffVersionFormData) => {
      // Note: The backend now handles all rate creation automatically:
      // - If base_version_id is provided, rates are copied from that version
      // - Otherwise, default rates are created using the 8-combination matrix:
      //   * 2 age types (Baby, Child)
      //   * 2 subsidised states (true, false)  
      //   * 2 booked states (booked, extra)
      //   = 8 combinations per active service (without weekdays)
      
      // Transform the form data to match the API request structure
      // Convert Date objects to shared_FlexibleDate format
      const tariffData: CreateTariffVersionRequest = {
        name: data.name,
        description: data.description,
        group_type: data.group_type,
        status: data.status, // Include status from form
        is_default: data.is_default,
        base_version_id: selectedVersion?.id, // Pass the source version ID for copying rates
        age_transition_months: data.age_transition_months,
        age_transition_months_subsidised: data.age_transition_months_subsidised,
        age_transition_timing:
          data.age_transition_timing as CreateTariffVersionRequest['age_transition_timing'],
        rounding_parents_enabled: data.rounding_parents_enabled,
        rounding_parents_amount: data.rounding_parents_amount,
        rounding_subsidy_enabled: data.rounding_subsidy_enabled,
        rounding_subsidy_amount: data.rounding_subsidy_amount,
        // Note: rates are now created automatically by the backend using the 8-combination matrix
        // The backend will create 8 combinations per active service without weekdays
        // If base_version_id is provided, rates will be copied from that version
      };
      
      const result = await createTariff.mutateAsync(tariffData);
      
      setShowCreateDialog(false);
      if (result?.id) {
        // Set the newly created tariff as selected
        // Note: result should contain the created tariff with its ID
        setSelectedVersion(result as TariffVersion);
        setViewMode("edit");
        
        // Refetch to ensure we have the latest data including rates
        refetch();
      }
    },
    [createTariff, refetch, selectedVersion?.id]
  );

  // Handle saving tariff rates with progress tracking
  const handleSaveRates = useCallback(
    async (rates: TariffRate[], options?: {
      onProgress?: (current: number, total: number, service?: string) => void;
      versionTag?: string;
    }) => {
      if (!selectedVersion?.id) return;

      await updateRates.mutateAsync({
        versionId: selectedVersion.id,
        // Pass the correct BulkUpdateTariffRatesRequest shape
        // Expected: { rates: TariffRateRequest[] }
        // The grid holds response-shaped rates; `toTariffRateRequests` narrows
        // `age_type` onto the request enum without touching `daily_rate`.
        rates: { rates: toTariffRateRequests(rates) },
        versionTag: options?.versionTag || selectedVersion.updated_at, // Use version's updated_at for optimistic locking
        onProgress: options?.onProgress, // Pass the progress callback
      });
    },
    [selectedVersion, updateRates]
  );


  // Handle applying bulk adjustments
  const handleApplyAdjustment = useCallback(
    (adjustment: { type: 'percentage' | 'fixed'; operation: 'increase' | 'decrease'; value: number; services?: string[]; days?: number[] }) => {
      // Set the pending adjustment to be processed by TariffUnifiedGrid
      setPendingAdjustment(adjustment);
    },
    []
  );

  // Handle copying rates between services
  const handleCopyRates = useCallback(
    (sourceService: string, targetService: string) => {
      // Set the pending copy to be processed by TariffUnifiedGrid
      setPendingCopy({ sourceService, targetService });
    },
    []
  );

  // Handle activating a tariff - for edit dialog
  const handleActivateFromDialog = useCallback(async (formData: TariffVersionFormInput) => {
    if (!editingVersion?.id) return;

    // First, save any form changes
    const updateData: billing_UpdateTariffVersionRequest = {
      name: formData.name,
      description: formData.description,
      status: formData.status as billing_UpdateTariffVersionRequest['status'],
      age_transition_months: formData.age_transition_months,
      age_transition_months_subsidised: formData.age_transition_months_subsidised,
      age_transition_timing:
        formData.age_transition_timing as billing_UpdateTariffVersionRequest['age_transition_timing'],
      rounding_parents_enabled: formData.rounding_parents_enabled,
      rounding_parents_amount: formData.rounding_parents_amount,
      rounding_subsidy_enabled: formData.rounding_subsidy_enabled,
      rounding_subsidy_amount: formData.rounding_subsidy_amount,
    };

    await saveBeforeActivate.mutateAsync({ id: editingVersion.id, data: updateData });

    // Then activate the tariff
    await activateTariff.mutateAsync(editingVersion.id);

    setShowEditDialog(false);
    setEditingVersion(null);
    refetch();
  }, [editingVersion, saveBeforeActivate, activateTariff, refetch]);

  // Handle filter changes from the table
  const handleFilterChange = useCallback((filters: ColumnFiltersState) => {
    // Status is now handled by the Current/All toggle, so we only process non-status filters here
    const newFilters: { status?: string } = {};
    void filters;
    setServerFilters(newFilters);
  }, []);

  // Handle sorting changes from the table
  const handleSortingChange = useCallback((sorting: SortingState) => {
    if (sorting.length > 0) {
      const { id, desc } = sorting[0];
      const backendColumn = SORT_COLUMN_MAP[id];
      if (backendColumn) {
        setServerSorting({
          sortBy: backendColumn,
          sortOrder: desc ? 'desc' : 'asc',
        });
      }
    } else {
      // Reset to default sorting
      setServerSorting({
        sortBy: 'created_at',
        sortOrder: 'desc',
      });
    }
  }, []);

  // Handle search changes from the table (debounced in BaseEditableTable)
  const handleSearchChange = useCallback((search: string) => {
    setServerSearch(search);
  }, []);

  // Handle inline cell updates from the table
  const handleCellUpdate = useCallback(
    async (rowId: string, field: keyof TariffVersion, newValue: unknown) => {
      // Find the tariff version to update
      const version = tariffs?.find(t => t.id === rowId);
      if (!version) return;

      // Build update data with the changed field
      const updateData: Record<string, unknown> = {
        name: version.name,
        description: version.description,
        status: version.status,
      };

      // Update the specific field
      if (field === 'groupType' || field === 'group_type') {
        updateData.group_type = newValue;
      } else if (field === 'isDefault' || field === 'is_default') {
        updateData.is_default = newValue;
      } else if (field === 'status') {
        updateData.status = newValue;
      }

      await updateTariff.mutateAsync({ id: rowId, data: updateData });
      refetch();
    },
    [tariffs, updateTariff, refetch]
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex-none">
          <div className="pb-4">
            <Skeleton className="h-10 w-48" />
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          <div className="space-y-6">
            <div className="grid gap-4">
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <QueryErrorState
        error={error}
        onRetry={() => void refetch()}
        title={<Trans id="tariffs.error">Error loading tariffs</Trans>}
      />
    );
  }

  return (
    <>
      <div className="space-y-6">
        {viewMode === "list" ? (
          <>
            {/* Add New Button */}
            <div className="flex justify-end">
              <Button onClick={handleCreateNew}>
                <Plus className="mr-2 h-4 w-4" />
                <Trans id="common.add">Add</Trans>
              </Button>
            </div>

            {/* Tariff Versions Table with Server-Side Pagination */}
            <TariffVersionsTable
              data={tariffs || []}
              onDetails={handleEdit}
              onCellUpdate={handleCellUpdate}
              enableSelection={true}
              serverSidePagination={true}
              totalRows={total}
              pagination={pagination}
              onPaginationChange={onPaginationChange}
              isFetching={isFetching}
              onFilterChange={handleFilterChange}
              onSortingChange={handleSortingChange}
              onSearchChange={handleSearchChange}
              serverFilters={serverFilters}
              serverSorting={serverSorting}
              toolbarRight={
                <SegmentedToggle
                  className="ml-auto"
                  value={showCurrent ? "current" : "all"}
                  onValueChange={(next) => setShowCurrent(next === "current")}
                  aria-label={i18n._({ id: "tariffs.filter.scope", message: "Which tariff versions to show" })}
                  options={[
                    { value: "current", label: i18n._({ id: "children.filter.current", message: "Current" }) },
                    { value: "all", label: i18n._({ id: "children.filter.all", message: "All" }) },
                  ]}
                />
              }
            />
          </>
        ) : (
        <>
          {/* Back button */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => {
                setViewMode("list");
                setSelectedVersion(null);
              }}
            >
              <Trans id="tariffs.actions.backToList">Back to List</Trans>
            </Button>
          </div>

          {/* Unified Tariff Grid */}
          {selectedVersion && (
            <>
              <TariffUnifiedGrid
                versionName={selectedVersion.name}
                versionDescription={selectedVersion.description}
                groupType={selectedVersion.groupType || GroupType.STANDARD}
                status={selectedVersion.status}
                isDefault={selectedVersion.is_default || selectedVersion.isDefault}
                rates={selectedRates || []}
                onSave={handleSaveRates}
                isLoading={ratesLoading || updateRates.isPending}
                readOnly={false}
                pendingAdjustment={pendingAdjustment}
                onAdjustmentApplied={() => setPendingAdjustment(null)}
                pendingCopy={pendingCopy}
                onCopyApplied={() => setPendingCopy(null)}
                versionUpdatedAt={selectedVersion.updated_at}
                enableChunking={true}
              />
              
              {/* Bulk Actions Card */}
              <TariffBulkActions
                versionId={selectedVersion.id || ""}
                currentRates={selectedRates || []}
                onApplyAdjustment={handleApplyAdjustment}
                onCopyRates={handleCopyRates}
                onUpdate={() => {}}
                isDisabled={false}
              />
            </>
          )}
        </>
        )}

      {/* Create/Copy Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent size="xl">
          <DialogHeader>
            <DialogTitle>
              {selectedVersion
                ? i18n._({
                    id: "tariffs.dialog.copyTitle",
                    message: "Copy Tariff Version",
                  })
                : i18n._({
                    id: "tariffs.dialog.createTitle",
                    message: "Create New Tariff Version",
                  })}
            </DialogTitle>
            <DialogDescription>
              {selectedVersion
                ? i18n._({
                    id: "tariffs.dialog.copyDescription",
                    message: "Create a new tariff version based on an existing one.",
                  })
                : i18n._({
                    id: "tariffs.dialog.createDescription",
                    message: "Configure a new tariff version with rates and services.",
                  })}
            </DialogDescription>
          </DialogHeader>
          <TariffVersionFormWithEnums
            onSubmit={handleSaveNewTariff}
            onCancel={() => setShowCreateDialog(false)}
            initialData={selectedVersion || undefined}
            isCopy={!!selectedVersion}
            isLoading={createTariff.isPending}
            versions={tariffs || []}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent size="xl">
          <DialogHeader>
            <DialogTitle>
              {i18n._({
                id: "tariffs.dialog.editTitle",
                message: "Edit Tariff Version",
              })}
            </DialogTitle>
            <DialogDescription>
              {i18n._({
                id: "tariffs.dialog.editDescription",
                message: "Update tariff version details and settings.",
              })}
            </DialogDescription>
          </DialogHeader>
          <TariffVersionFormWithEnums
            onSubmit={handleSaveEditedTariff}
            onCancel={() => {
              setShowEditDialog(false);
              setEditingVersion(null);
            }}
            initialData={editingVersion || undefined}
            isEdit={true}
            onActivate={handleActivateFromDialog}
            isActivating={saveBeforeActivate.isPending || activateTariff.isPending}
            canActivate={editingVersion?.status === "DRAFT" || editingVersion?.status === "ACTIVE"}
            isLoading={updateTariff.isPending}
            onCopy={() => {
              if (editingVersion) {
                setShowEditDialog(false);
                handleCopy(editingVersion);
              }
            }}
            canCopy={true}
            onArchive={async () => {
              if (editingVersion) {
                await handleArchive(editingVersion);
                setShowEditDialog(false);
                setEditingVersion(null);
              }
            }}
            isArchiving={archiveTariff.isPending}
            canArchive={editingVersion?.status !== "ARCHIVED"}
            versions={tariffs || []}
            onEditRates={() => {
              if (editingVersion) {
                setShowEditDialog(false);
                setEditingVersion(null);
                handleView(editingVersion);
              }
            }}
            canEditRates={true}
          />
        </DialogContent>
      </Dialog>
      </div>
    </>
  );
}
